import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslator } from '@/hooks/useTranslator';
import { avatarColorClass, getCompanyInitials } from '@/lib/utils/avatar';
import { cn } from '@/lib/utils/cn';
import type { UserMenuCompany } from './types';

interface UserMenuCompanySwitcherProps {
  /** Already filtered by `search` — the caller owns the query. */
  companies: UserMenuCompany[];
  search: string;
  onSearchChange: (value: string) => void;
  onSelect: (company: UserMenuCompany) => void;
}

/** Search box plus the list of companies the user can act for. */
export function UserMenuCompanySwitcher({
  companies,
  search,
  onSearchChange,
  onSelect,
}: UserMenuCompanySwitcherProps) {
  const { getTranslation } = useTranslator({ page: 'appHeader' });

  return (
    <>
      <div className='px-2 py-1.5'>
        <div className='relative'>
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className='absolute left-2.5 top-1/2 -translate-y-1/2 size-3 text-muted-foreground pointer-events-none'
          />
          <Input
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            placeholder={getTranslation('searchCompanies')}
            className='h-7 pl-7 text-xs'
          />
        </div>
      </div>

      <p className='px-3 pt-1 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
        {getTranslation('switchCompany')}
      </p>

      <div className='max-h-65 overflow-y-auto pb-1'>
        {companies.map(company => (
          <Button
            key={company.companyId}
            variant='ghost'
            className='w-full justify-start gap-2.5 px-3 py-2 h-auto text-sm font-normal rounded-none'
            onClick={() => onSelect(company)}
          >
            {/* Rounded square, as a company `EntityAvatar`: the corner is the
                token for a 32px box. */}
            <Avatar className='h-8 w-8 shrink-0 rounded-sm after:rounded-sm'>
              <AvatarFallback
                delay={0}
                className={cn(
                  'rounded-sm text-xs font-semibold',
                  avatarColorClass(company.companyId),
                )}
              >
                {getCompanyInitials(company.name)}
              </AvatarFallback>
            </Avatar>
            <span className='truncate'>{company.name}</span>
          </Button>
        ))}
      </div>
    </>
  );
}
